import React, { useState, useEffect } from "react";
import {
  Settings,
  Star,
  Link2,
  Save,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  Clock,
  MessageSquare,
  Store,
  RefreshCw,
  Copy,
  Eye,
  Percent,
  MapPin,
  Plus,
  Trash2,
  Navigation,
  Loader2,
  Home,
  Building2,
  Edit3,
  X,
  DollarSign,
} from "lucide-react";
import { supabase } from "../supabaseClient";
import { sanitizeUrl } from "../utils/sanitize";

const StoreSettings = ({
  store,
  onClose,
  onSaved,
  campaignsTabEnabled = true,
  openSubscriptionModal,
  isSubscriptionDisabledForUser = true,
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [testingUrl, setTestingUrl] = useState(false);
  const [urlValid, setUrlValid] = useState(null);

  // Settings state
  const [settings, setSettings] = useState({
    google_review_url: "",
    google_place_id: "",
    review_threshold: 4,
    review_sms_enabled: true,
    review_delay_hours: 24,
    display_name: "",
    tagline: "Quality Furniture, Delivered With Care",
    tax_rate: 8.0,
  });

  // Store Addresses state
  const [addresses, setAddresses] = useState([]);
  const [loadingAddresses, setLoadingAddresses] = useState(true);
  const [showAddAddressForm, setShowAddAddressForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [addressForm, setAddressForm] = useState({
    name: "",
    address: "",
  });
  const [savingAddress, setSavingAddress] = useState(false);
  const [geocodingAddress, setGeocodingAddress] = useState(false);
  const [deletingAddressId, setDeletingAddressId] = useState(null);

  // Load existing settings
  useEffect(() => {
    if (store?.id) {
      loadSettings();
      loadAddresses();
    }
  }, [store?.id]);

  const loadSettings = async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from("store_settings")
        .select("*")
        .eq("store_id", store.id)
        .single();

      if (error && error.code !== "PGRST116") {
        throw error;
      }

      if (data) {
        setSettings({
          google_review_url: data.google_review_url || "",
          google_place_id: data.google_place_id || "",
          review_threshold: data.review_threshold || 4,
          review_sms_enabled: data.review_sms_enabled ?? true,
          review_delay_hours: data.review_delay_hours || 24,
          display_name: data.display_name || store?.name || "",
          tagline: data.tagline || "Quality Furniture, Delivered With Care",
          tax_rate:
            data.tax_rate !== undefined && data.tax_rate !== null
              ? data.tax_rate
              : 8.0,
        });

        if (data.google_review_url) {
          setUrlValid(isValidGoogleUrl(data.google_review_url));
        }
      } else {
        setSettings((prev) => ({
          ...prev,
          display_name: store?.name || "",
        }));
      }
    } catch (err) {
      console.error("Error loading settings:", err);
      setError("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const loadAddresses = async () => {
    try {
      setLoadingAddresses(true);

      const { data, error } = await supabase
        .from("store_addresses")
        .select("*")
        .eq("store_id", store.id)
        .order("is_primary", { ascending: false })
        .order("created_at", { ascending: true });

      if (
        error &&
        error.code !== "PGRST116" &&
        !error.message?.includes("does not exist")
      ) {
        throw error;
      }

      setAddresses(data || []);

      // If no addresses exist but store has an address, migrate it
      if (
        (!data || data.length === 0) &&
        (store?.address || store?.store_address)
      ) {
        await migrateStoreAddress();
      }
    } catch (err) {
      console.error("Error loading addresses:", err);
      if (err.message?.includes("does not exist")) {
        setAddresses([]);
      }
    } finally {
      setLoadingAddresses(false);
    }
  };

  const migrateStoreAddress = async () => {
    const existingAddress = store?.address || store?.store_address;
    if (!existingAddress) return;

    try {
      const coords = await geocodeAddress(existingAddress);

      const { data, error } = await supabase
        .from("store_addresses")
        .insert({
          store_id: store.id,
          name: "Main Location",
          address: existingAddress,
          latitude: coords?.latitude || store?.latitude || null,
          longitude: coords?.longitude || store?.longitude || null,
          is_primary: true,
        })
        .select()
        .single();

      if (!error && data) {
        setAddresses([data]);
        console.log("✅ Migrated existing store address");
      }
    } catch (err) {
      console.error("Error migrating store address:", err);
    }
  };

  const geocodeAddress = async (address) => {
    if (!address) return null;

    setGeocodingAddress(true);

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`,
        {
          headers: {
            "User-Agent": "FurnitureDeliveryApp/1.0",
          },
        },
      );
      const data = await response.json();

      if (data && data.length > 0) {
        return {
          latitude: parseFloat(data[0].lat),
          longitude: parseFloat(data[0].lon),
        };
      }

      return null;
    } catch (err) {
      console.error("Geocoding error:", err);
      return null;
    } finally {
      setGeocodingAddress(false);
    }
  };

  const handleAddAddress = async () => {
    if (!addressForm.name.trim() || !addressForm.address.trim()) {
      setError("Please enter both a name and address");
      return;
    }

    setSavingAddress(true);
    setError("");

    try {
      const coords = await geocodeAddress(addressForm.address);
      const isPrimary = addresses.length === 0;

      const { data, error } = await supabase
        .from("store_addresses")
        .insert({
          store_id: store.id,
          name: addressForm.name.trim(),
          address: addressForm.address.trim(),
          latitude: coords?.latitude || null,
          longitude: coords?.longitude || null,
          is_primary: isPrimary,
        })
        .select()
        .single();

      if (error) throw error;

      setAddresses((prev) => [...prev, data]);
      setAddressForm({ name: "", address: "" });
      setShowAddAddressForm(false);
    } catch (err) {
      console.error("Error adding address:", err);
      setError("Failed to add address");
    } finally {
      setSavingAddress(false);
    }
  };

  const handleUpdateAddress = async () => {
    if (
      !editingAddress ||
      !addressForm.name.trim() ||
      !addressForm.address.trim()
    ) {
      setError("Please enter both a name and address");
      return;
    }

    setSavingAddress(true);
    setError("");

    try {
      const coords = await geocodeAddress(addressForm.address);

      const { data, error } = await supabase
        .from("store_addresses")
        .update({
          name: addressForm.name.trim(),
          address: addressForm.address.trim(),
          latitude: coords?.latitude || editingAddress.latitude,
          longitude: coords?.longitude || editingAddress.longitude,
        })
        .eq("id", editingAddress.id)
        .select()
        .single();

      if (error) throw error;

      setAddresses((prev) =>
        prev.map((a) => (a.id === editingAddress.id ? data : a)),
      );
      setAddressForm({ name: "", address: "" });
      setEditingAddress(null);
    } catch (err) {
      console.error("Error updating address:", err);
      setError("Failed to update address");
    } finally {
      setSavingAddress(false);
    }
  };

  const handleDeleteAddress = async (id) => {
    if (!window.confirm("Are you sure you want to delete this address?"))
      return;

    setDeletingAddressId(id);

    try {
      const { error } = await supabase
        .from("store_addresses")
        .delete()
        .eq("id", id);

      if (error) throw error;

      setAddresses((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      console.error("Error deleting address:", err);
      setError("Failed to delete address");
    } finally {
      setDeletingAddressId(null);
    }
  };

  const handleSetPrimary = async (id) => {
    try {
      // First, unset all primary flags
      await supabase
        .from("store_addresses")
        .update({ is_primary: false })
        .eq("store_id", store.id);

      // Set the new primary
      const { error } = await supabase
        .from("store_addresses")
        .update({ is_primary: true })
        .eq("id", id);

      if (error) throw error;

      setAddresses((prev) =>
        prev.map((a) => ({
          ...a,
          is_primary: a.id === id,
        })),
      );
    } catch (err) {
      console.error("Error setting primary:", err);
      setError("Failed to set primary address");
    }
  };

  const startEditAddress = (addr) => {
    setEditingAddress(addr);
    setAddressForm({ name: addr.name, address: addr.address });
    setShowAddAddressForm(false);
  };

  const cancelAddressForm = () => {
    setEditingAddress(null);
    setShowAddAddressForm(false);
    setAddressForm({ name: "", address: "" });
  };

  const isValidGoogleUrl = (url) => {
    if (!url) return false;
    const patterns = [
      /^https:\/\/g\.page\/r\/[A-Za-z0-9_-]+\/review/,
      /^https:\/\/search\.google\.com\/local\/writereview\?placeid=/,
      /^https:\/\/(www\.)?google\.com\/maps/,
      /^https:\/\/(www\.)?google\.com\/search\?.*#lrd=/,
    ];
    return patterns.some((pattern) => pattern.test(url));
  };

  const handleUrlChange = (e) => {
    const url = e.target.value;
    setSettings((prev) => ({ ...prev, google_review_url: url }));
    setUrlValid(url ? isValidGoogleUrl(url) : null);
    setSaved(false);
  };

  const testGoogleUrl = () => {
    const safeUrl = sanitizeUrl(settings.google_review_url);
    if (
      safeUrl &&
      (safeUrl.startsWith("https://") || safeUrl.startsWith("http://"))
    ) {
      setTestingUrl(true);
      window.open(safeUrl, "_blank", "noopener,noreferrer");
      setTimeout(() => setTestingUrl(false), 1000);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError("");

      if (
        campaignsTabEnabled &&
        settings.google_review_url &&
        !isValidGoogleUrl(settings.google_review_url)
      ) {
        setError("Please enter a valid Google Review URL");
        setSaving(false);
        return;
      }

      const { error } = await supabase.from("store_settings").upsert(
        {
          store_id: store.id,
          google_review_url: settings.google_review_url || null,
          google_place_id: settings.google_place_id || null,
          review_threshold: settings.review_threshold,
          review_sms_enabled: settings.review_sms_enabled,
          review_delay_hours: settings.review_delay_hours,
          display_name: settings.display_name || store?.name,
          tagline: settings.tagline,
          tax_rate: parseFloat(settings.tax_rate) || 8.0,
        },
        {
          onConflict: "store_id",
        },
      );

      if (error) throw error;

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);

      if (onSaved) onSaved(settings);
    } catch (err) {
      console.error("Error saving settings:", err);
      setError("Failed to save settings: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const copyTestUrl = () => {
    const testUrl = `${window.location.origin}/review?n=Test&s=${store?.id}&b=${encodeURIComponent(settings.display_name || store?.name)}&g=${encodeURIComponent(settings.google_review_url)}`;
    navigator.clipboard.writeText(testUrl);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center">
        <div className="flex items-center space-x-3 text-slate-700">
          <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
          <span>Loading settings...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
        {/* Header */}
        <div className="p-5 border-b border-slate-200 flex items-center justify-between bg-slate-50">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
              <Settings className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">
                Store Settings
              </h2>
              <p className="text-sm text-slate-500">{store?.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="w-10 h-10 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center hover:bg-slate-200 transition-colors text-slate-500 hover:text-slate-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  Subscription
                </p>
                <p className="text-xs text-slate-600 mt-1">
                  Manage your plan and billing options.
                </p>
              </div>
              <button
                type="button"
                onClick={openSubscriptionModal}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors"
              >
                <DollarSign className="w-4 h-4" />
                <span>View Plans</span>
              </button>
            </div>
          </div>
          {/* Store Addresses Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <MapPin className="w-5 h-5 text-blue-600" />
                <h3 className="text-lg font-semibold text-slate-900">
                  Store Addresses
                </h3>
              </div>
              {!showAddAddressForm && !editingAddress && (
                <button
                  onClick={() => setShowAddAddressForm(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-sm font-medium hover:bg-blue-100 transition-all"
                >
                  <Plus className="w-4 h-4" />
                  Add Address
                </button>
              )}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
              <p className="text-xs text-blue-700">
                <strong className="text-blue-800">Tip:</strong> The primary
                address is used as the starting point for delivery route
                optimization. Add multiple addresses if you have warehouses or
                pickup locations.
              </p>
            </div>

            {/* Address List */}
            {loadingAddresses ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
              </div>
            ) : addresses.length === 0 && !showAddAddressForm ? (
              <div className="text-center py-8">
                <MapPin className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 mb-3">No addresses configured</p>
                <button
                  onClick={() => setShowAddAddressForm(true)}
                  className="px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-sm font-medium hover:bg-blue-100 transition-all"
                >
                  Add Your First Address
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {addresses.map((addr) => (
                  <div
                    key={addr.id}
                    className={`p-3 rounded-xl border transition-all ${
                      addr.is_primary
                        ? "bg-emerald-50 border-emerald-200"
                        : "bg-slate-50 border-slate-200 hover:bg-slate-100"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          addr.is_primary ? "bg-emerald-100" : "bg-slate-200"
                        }`}
                      >
                        {addr.is_primary ? (
                          <Home className="w-5 h-5 text-emerald-600" />
                        ) : (
                          <Building2 className="w-5 h-5 text-slate-500" />
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="font-medium text-slate-900 truncate">
                            {addr.name}
                          </span>
                          {addr.is_primary && (
                            <span className="px-2 py-0.5 bg-emerald-100 border border-emerald-200 rounded-full text-xs text-emerald-700 font-medium whitespace-nowrap flex-shrink-0">
                              Primary
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-500 truncate">
                          {addr.address}
                        </p>
                        {addr.latitude && addr.longitude && (
                          <div className="flex items-center gap-1 mt-1">
                            <Navigation className="w-3 h-3 text-slate-400" />
                            <span className="text-xs text-slate-400 font-mono">
                              {addr.latitude.toFixed(4)},{" "}
                              {addr.longitude.toFixed(4)}
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-1 flex-shrink-0">
                        {!addr.is_primary && (
                          <button
                            onClick={() => handleSetPrimary(addr.id)}
                            className="p-2 rounded-lg bg-slate-100 hover:bg-emerald-100 text-slate-400 hover:text-emerald-600 transition-all"
                            title="Set as primary"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => startEditAddress(addr)}
                          className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-400 hover:text-slate-700 transition-all"
                          title="Edit"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteAddress(addr.id)}
                          disabled={deletingAddressId === addr.id}
                          className="p-2 rounded-lg bg-slate-100 hover:bg-red-50 text-slate-400 hover:text-red-600 transition-all disabled:opacity-50"
                          title="Delete"
                        >
                          {deletingAddressId === addr.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add/Edit Address Form */}
            {(showAddAddressForm || editingAddress) && (
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-slate-900">
                    {editingAddress ? "Edit Address" : "Add New Address"}
                  </h4>
                  <button
                    onClick={cancelAddressForm}
                    aria-label="Close address form"
                    className="p-1 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-700 transition-all"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div>
                  <label className="block text-xs text-slate-500 mb-1">
                    Name / Label
                  </label>
                  <input
                    type="text"
                    value={addressForm.name}
                    onChange={(e) =>
                      setAddressForm((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                    placeholder="e.g., Main Warehouse, Davis Store"
                    className="w-full px-3 py-2 rounded-lg bg-white border border-slate-200 text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs text-slate-500 mb-1">
                    Full Address
                  </label>
                  <input
                    type="text"
                    value={addressForm.address}
                    onChange={(e) =>
                      setAddressForm((prev) => ({
                        ...prev,
                        address: e.target.value,
                      }))
                    }
                    placeholder="e.g., 123 Main St, Davis, CA 95616"
                    className="w-full px-3 py-2 rounded-lg bg-white border border-slate-200 text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-sm"
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    Include city, state, and ZIP for GPS coordinates
                  </p>
                </div>

                <div className="flex gap-2 pt-2">
                  <button
                    onClick={cancelAddressForm}
                    className="flex-1 px-3 py-2 bg-slate-100 border border-slate-200 rounded-lg text-slate-600 text-sm font-medium hover:bg-slate-200 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={
                      editingAddress ? handleUpdateAddress : handleAddAddress
                    }
                    disabled={
                      savingAddress ||
                      !addressForm.name.trim() ||
                      !addressForm.address.trim()
                    }
                    data-tip="Saves this address and looks up its GPS location"
                    className="flex-1 px-3 py-2 bg-blue-600 rounded-lg text-white text-sm font-semibold hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {savingAddress ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {geocodingAddress ? "Getting GPS..." : "Saving..."}
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        {editingAddress ? "Update" : "Add Address"}
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>

          {campaignsTabEnabled && (
            <>
              {/* Divider */}
              <div className="border-t border-slate-200" />

              {/* Google Review Section */}
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Star className="w-5 h-5 text-amber-500" />
                  <h3 className="text-lg font-semibold text-slate-900">
                    Google Reviews
                  </h3>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm text-slate-600">
                    Google Review Link
                  </label>
                  <div className="flex space-x-2">
                    <div className="relative flex-1">
                      <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="url"
                        value={settings.google_review_url}
                        onChange={handleUrlChange}
                        placeholder="https://g.page/r/..."
                        className={`w-full pl-10 pr-4 py-3 rounded-xl bg-white border text-slate-900 placeholder-slate-400 focus:outline-none transition-colors ${
                          urlValid === true
                            ? "border-emerald-500 focus:ring-2 focus:ring-emerald-200"
                            : urlValid === false
                              ? "border-red-500 focus:ring-2 focus:ring-red-200"
                              : "border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                        }`}
                      />
                      {urlValid === true && (
                        <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-500" />
                      )}
                      {urlValid === false && (
                        <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-red-500" />
                      )}
                    </div>
                    <button
                      onClick={testGoogleUrl}
                      disabled={!settings.google_review_url || testingUrl}
                      aria-label="Open this Google review link in a new tab"
                      className="px-4 py-3 rounded-xl bg-slate-100 border border-slate-200 text-slate-700 hover:bg-slate-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-xs text-slate-400">
                    Get this from Google Business Profile → Share → "Ask for
                    reviews"
                  </p>
                </div>

                {/* Auto Review Settings */}
                <div className="flex items-center justify-between bg-slate-50 rounded-xl p-4 border border-slate-200">
                  <div className="flex items-center space-x-3">
                    <MessageSquare className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="text-sm font-medium text-slate-900">
                        Auto-send Review Requests
                      </p>
                      <p className="text-xs text-slate-500">
                        SMS customers after delivery
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() =>
                      setSettings((prev) => ({
                        ...prev,
                        review_sms_enabled: !prev.review_sms_enabled,
                      }))
                    }
                    aria-label={settings.review_sms_enabled ? "Turn off automatic review requests" : "Turn on automatic review requests"}
                    className={`relative w-14 h-8 rounded-full transition-colors ${
                      settings.review_sms_enabled
                        ? "bg-blue-600 shadow-lg shadow-blue-500/20"
                        : "bg-slate-300"
                    }`}
                  >
                    <div
                      className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow-lg transition-transform ${
                        settings.review_sms_enabled
                          ? "translate-x-7"
                          : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>

                {/* Delay Hours */}
                {settings.review_sms_enabled && (
                  <div className="space-y-2">
                    <label className="block text-sm text-slate-600">
                      <Clock className="w-4 h-4 inline mr-1" />
                      Hours after delivery to send review request
                    </label>
                    <select
                      value={settings.review_delay_hours}
                      onChange={(e) =>
                        setSettings((prev) => ({
                          ...prev,
                          review_delay_hours: parseInt(e.target.value),
                        }))
                      }
                      className="w-full px-4 py-3 rounded-xl bg-white border border-slate-200 text-slate-900 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors"
                    >
                      <option value={1}>1 hour</option>
                      <option value={2}>2 hours</option>
                      <option value={4}>4 hours</option>
                      <option value={12}>12 hours</option>
                      <option value={24}>24 hours (recommended)</option>
                      <option value={48}>48 hours</option>
                      <option value={72}>72 hours</option>
                    </select>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Display Settings */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Store className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-semibold text-slate-900">
                Review Page Display
              </h3>
            </div>

            <div className="space-y-2">
              <label className="block text-sm text-slate-600">
                Display Name
              </label>
              <input
                type="text"
                value={settings.display_name}
                onChange={(e) =>
                  setSettings((prev) => ({
                    ...prev,
                    display_name: e.target.value,
                  }))
                }
                placeholder={store?.name || "Store Name"}
                className="w-full px-4 py-3 rounded-xl bg-white border border-slate-200 text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors"
              />
              <p className="text-xs text-slate-400">
                This appears at the top of the review page
              </p>
            </div>

            <div className="space-y-2">
              <label className="block text-sm text-slate-600">Tagline</label>
              <input
                type="text"
                value={settings.tagline}
                onChange={(e) =>
                  setSettings((prev) => ({ ...prev, tagline: e.target.value }))
                }
                placeholder="Quality Furniture, Delivered With Care"
                className="w-full px-4 py-3 rounded-xl bg-white border border-slate-200 text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors"
              />
            </div>
          </div>

          {/* Tax Settings */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Percent className="w-5 h-5 text-emerald-600" />
              <h3 className="text-lg font-semibold text-slate-900">
                Tax Settings
              </h3>
            </div>

            <div className="space-y-2">
              <label className="block text-sm text-slate-600">
                Tax Rate (%)
              </label>
              <div className="relative">
                <input
                  type="text"
                  inputMode="decimal"
                  value={settings.tax_rate}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "" || /^\d*\.?\d*$/.test(value)) {
                      setSettings((prev) => ({ ...prev, tax_rate: value }));
                      setSaved(false);
                    }
                  }}
                  onBlur={(e) => {
                    const num = parseFloat(e.target.value);
                    if (!isNaN(num)) {
                      setSettings((prev) => ({
                        ...prev,
                        tax_rate: num.toFixed(2),
                      }));
                    }
                  }}
                  placeholder="8.00"
                  className="w-full px-4 py-3 pr-12 rounded-xl bg-white border border-slate-200 text-slate-900 placeholder-slate-400 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 transition-colors"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 font-medium">
                  %
                </span>
              </div>
              <p className="text-xs text-slate-400">
                This tax rate will be applied to all new receipts. Current rate:{" "}
                {parseFloat(settings.tax_rate) || 0}%
              </p>

              {/* Quick select common rates */}
              <div className="flex flex-wrap gap-2 mt-2">
                {[0, 6, 7, 7.25, 7.5, 8, 8.25, 8.5, 9, 9.5, 10].map((rate) => (
                  <button
                    key={rate}
                    onClick={() => {
                      setSettings((prev) => ({
                        ...prev,
                        tax_rate: rate.toFixed(2),
                      }));
                      setSaved(false);
                    }}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      parseFloat(settings.tax_rate) === rate
                        ? "bg-emerald-100 border border-emerald-300 text-emerald-700"
                        : "bg-slate-100 border border-slate-200 text-slate-600 hover:bg-slate-200 hover:text-slate-900"
                    }`}
                  >
                    {rate}%
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Preview / Test */}
          {campaignsTabEnabled && settings.google_review_url && urlValid && (
            <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-slate-900">
                  Test Your Review Page
                </p>
                <button
                  onClick={copyTestUrl}
                  data-tip="Copies the test review page link to your clipboard"
                  className="flex items-center space-x-1 text-xs text-blue-600 hover:text-blue-700"
                >
                  <Copy className="w-3 h-3" />
                  <span>Copy URL</span>
                </button>
              </div>
              <p className="text-xs text-slate-500 mb-3">
                Open this link to see what customers will see:
              </p>
              <a
                href={`/review?n=Test&s=${store?.id}&b=${encodeURIComponent(settings.display_name || store?.name)}&g=${encodeURIComponent(settings.google_review_url)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-tip="Opens a test version of your review page in a new tab"
                className="flex items-center justify-center space-x-2 w-full py-3 rounded-xl bg-white border border-blue-200 text-blue-700 hover:bg-blue-100 transition-colors font-medium"
              >
                <Eye className="w-4 h-4" />
                <span>Preview Review Page</span>
              </a>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="flex items-center space-x-2 p-4 bg-red-50 border border-red-200 rounded-xl">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
          <div className="text-sm text-slate-500 text-center">
            {" "}
            Beta Version: {__APP_VERSION__}
          </div>
        </div>

        {/* Warning for missing Google Review URL */}
        {campaignsTabEnabled &&
          settings.review_sms_enabled &&
          !settings.google_review_url && (
            <div className="mx-5 mb-0 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-center space-x-2">
              <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
              <p className="text-xs text-amber-700">
                Auto-review SMS is enabled but no Google Review URL is set.
                Review requests won't be sent until you add one.
              </p>
            </div>
          )}
        {/* Footer */}
        <div className="p-5 border-t border-slate-200 flex items-center justify-between bg-slate-50">
          <button
            onClick={onClose}
            className="px-6 py-3 rounded-xl bg-slate-100 border border-slate-200 text-slate-700 hover:bg-slate-200 transition-colors font-semibold"
          >
            Cancel
          </button>

          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-3 rounded-xl font-semibold flex items-center justify-center space-x-2 transition-all bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-500/20 disabled:opacity-50 disabled:cursor-not-allowed min-w-[140px]"
          >
            {saving ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : saved ? (
              <>
                <CheckCircle className="w-4 h-4" />
                <span>Saved!</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span>Save</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StoreSettings;
